'use client';

import { isBefore } from 'date-fns';
import type { Route } from 'next';
import { useRouter, useSearchParams } from 'next/navigation';
import ActionButton from '../../../../components/form/ActionButton/ActionButton';
import ImageView from '../../../../components/ui/ImageView/ImageView';
import WChanceStaging from '../../../../components/ui/WChanceStaging/WChanceStaging';
import { Format } from '../../../../lib/dateFns';
import fontsStyles from '../../../../styles/modules/fonts.module.scss';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import { newDate } from '../../../../utils/date';
import { formatDateCommon } from '../../../../utils/formatters';
import type { LotteryOptionGoods } from '../../types';
import { createStagingInfo } from '../../utils';
import styles from './OptionRequiredCard.module.scss';

export type OptionRequiredCardProps = {
  goodsList: LotteryOptionGoods;
  lotteryHistoryId?: number;
  lotteryPrizeGoodsId?: number;
  lotteryCutoffId?: number;
  orderId?: string;
};

function OptionRequiredCard({
  goodsList,
  lotteryHistoryId,
  lotteryPrizeGoodsId,
  lotteryCutoffId,
  orderId,
}: OptionRequiredCardProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const handleOptionInputClick = (goodsId: number, cutoffId: number, isEditMode = false) => {
    const queryParams = new URLSearchParams();
    queryParams.set('goodsId', goodsId.toString());
    queryParams.set('cutoffId', cutoffId.toString());

    if (isEditMode) {
      queryParams.set('edit', 'true');
    }

    if (lotteryHistoryId) {
      queryParams.set('lotteryHistoryId', lotteryHistoryId.toString());
    }
    if (lotteryPrizeGoodsId) {
      queryParams.set('lotteryPrizeGoodsId', lotteryPrizeGoodsId.toString());
    }
    if (orderId) {
      queryParams.set('orderId', orderId);
    }
    if (lotteryCutoffId) {
      queryParams.set('lotteryCutoffId', lotteryCutoffId.toString());
    }

    // 遷移元コンテキスト（from）を持ち回す
    const from = searchParams.get('from');
    if (from) {
      queryParams.set('from', from);
    }
    const url = `/account/collection/option/input?${queryParams.toString()}` as Route;
    router.push(url);
    router.refresh();
  };

  return (
    <div className={`${styles.optionRequiredCardList} `}>
      {goodsList.labels
        .filter((label) => label.goods && label.goods.length > 0)
        .flatMap((label) =>
          formattedItems(label.goods, label.stagingKey, label.benefitMultiDrawCount ?? 0, label.isWchance),
        )
        .map((item) => {
          const remainingCount = item.totalAcquiredCount - item.inputtedCount;
          // 負の値になることはないが、念のため0以下の場合は入力済みとして扱う (CursorBotによる指摘のため)
          const isOptionCompleted = remainingCount <= 0;

          return (
            <div key={item.id} className={`${styles.itemCard}`}>
              <div>
                <div className={`${styles.itemDetailWrapper} ${utilsStyles.mtXSmall}`}>
                  <div className={`${styles.itemDetailWrapperInner}`}>
                    <div className={styles.itemImage}>
                      <ImageView alt={item.name} src={item.imageUrl} />
                    </div>
                    <div className={`${styles.itemDetail} ${utilsStyles.mlSmall}`}>
                      <div
                        className={`${styles.optionStatus} ${isOptionCompleted ? utilsStyles.warmgrayColor : utilsStyles.primaryColor}`}
                      >
                        {isOptionCompleted ? 'オプション入力済み' : 'オプション未入力'}
                      </div>
                      {/* ステージング表示 */}
                      <div className={styles.stagingRankWrapper}>{staging(item)}</div>
                      <div className={styles.itemNameWrapper}>
                        <p className={`${styles.itemName} ${utilsStyles.textBold}`}>{item.name}</p>
                      </div>

                      {/* 入力済みのオプションの場合は、◯/◯個表記表示、ならびに、入力期限を黒字で表示 */}
                      {isOptionCompleted && (
                        <>
                          <div className={`${utilsStyles.textBold} ${utilsStyles.mtXSmall} ${utilsStyles.textMedium}`}>
                            入力済み：
                            <span className={`${styles.inputtedCount}`}>
                              <span className={`${utilsStyles.textXLarge}`}>{item.inputtedCount}</span>/
                              <span className={`${utilsStyles.textBold}`}>{item.totalAcquiredCount}個</span>
                            </span>
                          </div>
                          <div
                            className={`${utilsStyles.textSmall} ${utilsStyles.mtXSmall} ${styles.optionCutoffAt} ${utilsStyles.textBold}`}
                          >
                            入力期限：
                            {item.optionCutoffAt ? formatDateCommon(item.optionCutoffAt, Format.dateTimeSlash) : ''}
                          </div>
                        </>
                      )}

                      {/* 未入力のオプションがある場合のみ未入力件数と入力期限を赤字で表示 */}
                      {!isOptionCompleted && (
                        <>
                          <div
                            className={`${utilsStyles.textBold} ${utilsStyles.mtXSmall} ${utilsStyles.textMedium} ${utilsStyles.primaryColor}`}
                          >
                            未入力：
                            <span className={`${styles.inputtedCount}`}>
                              <span className={`${utilsStyles.textXLarge}`}>{remainingCount}</span>
                              <span className={`${utilsStyles.textBold}`}>個</span>
                            </span>
                          </div>
                          <div
                            className={`${utilsStyles.textSmall} ${utilsStyles.mtXSmall} ${styles.optionCutoffAt} ${utilsStyles.primaryColor} ${utilsStyles.textBold}`}
                          >
                            入力期限：
                            {item.optionCutoffAt ? formatDateCommon(item.optionCutoffAt, Format.dateTimeSlash) : ''}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className={`${styles.optionInputButtonWrapper} `}>
                    {!isOptionCompleted ? (
                      <ActionButton
                        size='fullWidth'
                        color='primary'
                        onClick={() => handleOptionInputClick(item.id, item.cutoffId, false)}
                      >
                        オプション入力をする
                      </ActionButton>
                    ) : (
                      <ActionButton
                        size='fullWidth'
                        color='black'
                        outlined
                        onClick={() => handleOptionInputClick(item.id, item.cutoffId, true)}
                      >
                        {item.optionCutoffAt && isBefore(newDate(item.optionCutoffAt), newDate())
                          ? '入力内容を確認する'
                          : '入力内容を変更する'}
                      </ActionButton>
                    )}
                  </div>
                  {item.optionCutoffAt && isBefore(newDate(item.optionCutoffAt), newDate()) && (
                    <div
                      className={`${utilsStyles.textSmall} ${utilsStyles.mtXSmall} ${utilsStyles.centeredVertical} ${utilsStyles.primaryColor} ${utilsStyles.fullWidth}`}
                    >
                      <p>※入力期限外のため、変更はできません </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
}

function formattedItems(
  items: LotteryOptionGoods['labels'][0]['goods'],
  stagingKey: string,
  benefitMultiDrawCount: number,
  isWchance: boolean,
) {
  const result = items.map((item) => {
    const stagingInfo = createStagingInfo(stagingKey);
    const formattedItem = {
      ...item,
      ...stagingInfo,
      benefitMultiDrawCount,
      isWchance,
    };
    return formattedItem;
  });

  return result;
}

function getRankStyle(rank: number) {
  switch (rank) {
    case 1:
      return styles.rank1;
    case 2:
      return styles.rank2;
    case 3:
      return styles.rank3;
    default:
      return styles.defaultRankImage;
  }
}

function staging(
  item: LotteryOptionGoods['labels'][0]['goods'][0] & {
    stagingRank: string;
    stagingText: string;
    benefitMultiDrawCount: number;
    isWchance: boolean;
  },
) {
  // X連特典
  if (item.benefitMultiDrawCount > 0) {
    return (
      <div className={styles.benefitWrapper}>
        <span className={fontsStyles.montserrat}>{item.benefitMultiDrawCount}</span>
        <small>連特典</small>
      </div>
    );
  }
  if (item.isWchance) {
    // Wチャンス
    return <WChanceStaging size={'default'} />;
  }
  if (item.stagingRank) {
    return (
      <div className={`${styles.stagingRank} ${getRankStyle(Number(item.stagingRank))} `}>
        <p className={`${styles.stagingText} ${fontsStyles.montserrat}`}>
          {item.stagingText}
          <small>賞</small>
        </p>
      </div>
    );
  }

  return <></>;
}

export default OptionRequiredCard;
